import React, { useState, useEffect } from "react";
import { PhoneIncoming, UserCheck, Calendar, GitBranch, PhoneOutgoing, Database, Activity, ShieldCheck, Zap } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const capabilities = [{
  title: "AI Call Answering",
  description: "Handle inbound calls 24/7 with human-like voice agents that never sleep or take breaks.",
  icon: PhoneIncoming
}, {
  title: "Lead Qualification",
  description: "Instantly qualify prospects based on custom criteria and route high-value leads to your team.",
  icon: UserCheck
}, {
  title: "Appointment Scheduling",
  description: "Seamlessly book meetings directly into your calendar while speaking with customers.",
  icon: Calendar
}, {
  title: "Smart Call Routing",
  description: "Intelligently transfer calls to the right department or specific person based on caller intent.",
  icon: GitBranch
}, {
  title: "Outbound Automation",
  description: "Run automated campaigns for appointment reminders, follow-ups, and cold outreach.",
  icon: PhoneOutgoing
}, {
  title: "CRM Integration",
  description: "Sync call data, transcripts, and customer details with your CRM in real-time.",
  icon: Database
}, {
  title: "Real-time Analytics",
  description: "Gain actionable insights from every conversation with advanced sentiment analysis.",
  icon: Activity
}, {
  title: "Enterprise Security",
  description: "Bank-grade encryption and compliance standards to keep your business data secure.",
  icon: ShieldCheck
}, {
  title: "Instant Scalability",
  description: "Handle infinite concurrent calls without hiring more staff or expanding infrastructure.",
  icon: Zap
}];
export default function PlatformCapabilities() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="platform-capabilities" ref={ref} className="bg-secondary py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className={`text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            Platform Capabilities
          </h2>
          <p className={`text-lg text-muted-foreground transition-all duration-700 delay-100 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            Discover the power of an enterprise-grade AI voice platform designed to automate, optimize, and scale your business communications.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((item, i) => <CapabilityCard key={i} item={item} index={i} sectionVisible={isVisible} />)}
        </div>
      </div>
    </section>;
}
function CapabilityCard({
  item,
  index,
  sectionVisible
}: {
  item: typeof capabilities[0];
  index: number;
  sectionVisible: boolean;
}) {
  const [hasAnimated, setHasAnimated] = useState(false);
  const delay = index * 100;
  useEffect(() => {
    if (sectionVisible && !hasAnimated) {
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [sectionVisible, delay, hasAnimated]);
  return <div data-index={index} className={`transition-all duration-700 ease-out transform ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      <Card className="h-full relative overflow-hidden border-border/50 bg-card hover:shadow-lg transition-shadow duration-300 group">
        {}
        <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary/20 blur-[50px] rounded-full pointer-events-none group-hover:bg-primary/30 transition-colors duration-500" />
        
        <CardHeader className="relative z-10 pb-2">
          <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary">
            <item.icon className={`w-6 h-6 transition-all duration-700 ${hasAnimated ? 'animate-icon-loop' : ''}`} />
          </div>
          <CardTitle className="text-xl font-bold text-foreground">
            {item.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="relative z-10">
          <CardDescription className="text-base text-muted-foreground leading-relaxed">
            {item.description}
          </CardDescription>
        </CardContent>
      </Card>
      
      {}
      <style jsx>{`
        @keyframes icon-loop {
          0% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.1) rotate(-10deg); }
          50% { transform: scale(1.1) rotate(10deg); }
          75% { transform: scale(1.1) rotate(-5deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        .animate-icon-loop {
          animation: icon-loop 1s ease-in-out forwards;
        }
      `}</style>
    </div>;
}